import { useEffect } from 'react';
import { breakpoints, Inline, Input, Stack } from '@etg/wings';
import { Checkbox } from '@eti/form';
import { useSearch, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import type { MapDispatchToProps, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FormStateMap } from 'redux-form';
import { change as changeFormValue, formValueSelector } from 'redux-form';
import { resetAllFilters } from '../../../pages/result/components/search-filters/utils/searchFilters';
import { stopoverFields } from '../constants/formFields';
import {
  STOPOVER_TYPES,
  SHORT_STOPOVER as SHORT_FILTER,
  ONE_DAY_STOPOVER as ONE_DAY_FILTER,
  ONE_NIGHT_STOPOVER as ONE_NIGHT_FILTER,
  TWO_NIGHTS_STOPOVER as TWO_NIGHTS_FILTER,
} from '../../../constants/searchFiltersConstants';

const { ENABLED, ONE_DAY, ONE_NIGHT, SHORT, TWO_NIGHTS } = stopoverFields;

const fieldSetStyles = css`
  border: none;
  padding-top: 1rem;
`;

const topSpacingStyles = css`
  margin-top: 1rem;
`;

type StopoverType =
  | typeof SHORT_FILTER
  | typeof ONE_DAY_FILTER
  | typeof ONE_NIGHT_FILTER
  | typeof TWO_NIGHTS_FILTER;

type ConnectedStateProps = {
  isTopStopoverControlSelected: boolean;
  isShortStopoverSelected: boolean;
  isOneDayStopoverSelected: boolean;
  isOneNightStopoverSelected: boolean;
  isTwoNightsStopoverSelected: boolean;
};

interface DispatchProps {
  updateAllStopovers: (isStopoverEnabled: boolean) => void;
  updateStopover: (stopover: string, isSelected: boolean) => void;
}

type OwnProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  form: string;
  withTopSpacing: boolean;
};

type StopoverSelectProps = ConnectedStateProps & DispatchProps & OwnProps;

const StopoverSelect = ({
  withTopSpacing,
  isTopStopoverControlSelected,
  updateAllStopovers,
  updateStopover,
  isShortStopoverSelected,
  isOneDayStopoverSelected,
  isOneNightStopoverSelected,
  isTwoNightsStopoverSelected,
}: StopoverSelectProps) => {
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const stopoversCollection = [
    isShortStopoverSelected,
    isOneDayStopoverSelected,
    isOneNightStopoverSelected,
    isTwoNightsStopoverSelected,
  ];

  const updateStopoverSearchData = (stopover: StopoverType) => {
    if (!setSearchData) {
      return;
    }

    const stopoverTypes = currentSearchData?.filters?.[STOPOVER_TYPES];
    const isStopoverEnabled = stopoverTypes?.includes(stopover);
    const filteredStopovers = stopoverTypes?.filter((item) => item !== stopover);
    const updatedStopovers = Array.isArray(stopoverTypes)
      ? [...stopoverTypes, stopover]
      : [stopover];

    if (!filteredStopovers?.length) {
      setSearchData({
        filters: {
          ...currentSearchData?.filters,
          [STOPOVER_TYPES]: null,
        },
      });
      return;
    }

    setSearchData({
      filters: {
        ...currentSearchData?.filters,
        [STOPOVER_TYPES]: isStopoverEnabled ? filteredStopovers : updatedStopovers,
      },
    });
  };

  const updateAllStopoversSearchData = () => {
    if (!setSearchData) {
      return;
    }

    if (isTopStopoverControlSelected) {
      setSearchData({ filters: { ...resetAllFilters(currentSearchData?.filters) } });
      return;
    }

    setSearchData({
      filters: {
        ...currentSearchData?.filters,
        [STOPOVER_TYPES]: [SHORT_FILTER, ONE_DAY_FILTER, ONE_NIGHT_FILTER, TWO_NIGHTS_FILTER],
      },
    });
  };

  useEffect(() => {
    if (stopoversCollection.every((stopover) => !stopover)) {
      updateStopover(ENABLED, false);
    }
  });

  return (
    <div>
      <Inline className={withTopSpacing ? topSpacingStyles : undefined} spacing={8}>
        <Checkbox
          data-testid={`${ENABLED}-input`}
          id="stopoverCheckbox"
          name={ENABLED}
          onChange={() => {
            updateAllStopovers(!isTopStopoverControlSelected);
            updateAllStopoversSearchData();
          }}
        />
        <Input.Label htmlFor="stopoverCheckbox">{t('Searchform.Stopover.City')}</Input.Label>
      </Inline>
      {isTopStopoverControlSelected && (
        <fieldset className={fieldSetStyles}>
          <Stack spacing={8}>
            <legend>{t('Searchform.Stopover.LengthOfStay.Text')}</legend>
            <Inline collapseBelow={breakpoints._768} spacing={16}>
              <Inline spacing={0}>
                <Checkbox
                  data-testid={`${SHORT}-input`}
                  id="shortStopCheckboxSearchForm"
                  name={SHORT}
                  onChange={() => {
                    updateStopover(SHORT, isShortStopoverSelected);
                    updateStopoverSearchData(SHORT_FILTER);
                  }}
                />
                <Input.Label htmlFor="shortStopCheckboxSearchForm">
                  {t('Searchform.Stopover.ShortStop')}
                </Input.Label>
              </Inline>

              <Inline spacing={0}>
                <Checkbox
                  data-testid={`${ONE_DAY}-input`}
                  id="oneDayStopCheckboxSearchForm"
                  name={ONE_DAY}
                  onChange={() => {
                    updateStopover(ONE_DAY, isOneDayStopoverSelected);
                    updateStopoverSearchData(ONE_DAY_FILTER);
                  }}
                />
                <Input.Label htmlFor="oneDayStopCheckboxSearchForm">
                  {t('Searchform.Stopover.OneDayStop')}
                </Input.Label>
              </Inline>

              <Inline spacing={0}>
                <Checkbox
                  data-testid={`${ONE_NIGHT}-input`}
                  id="oneNightStopCheckboxSearchForm"
                  name={ONE_NIGHT}
                  onChange={() => {
                    updateStopover(ONE_NIGHT, isOneNightStopoverSelected);
                    updateStopoverSearchData(ONE_NIGHT_FILTER);
                  }}
                />
                <Input.Label htmlFor="oneNightStopCheckboxSearchForm">
                  {t('Searchform.Stopover.OneNightStop')}
                </Input.Label>
              </Inline>

              <Inline spacing={0}>
                <Checkbox
                  data-testid={`${TWO_NIGHTS}-input`}
                  id="twoNightsStopCheckboxSearchForm"
                  name={TWO_NIGHTS}
                  onChange={() => {
                    updateStopover(TWO_NIGHTS, isTwoNightsStopoverSelected);
                    updateStopoverSearchData(TWO_NIGHTS_FILTER);
                  }}
                />
                <Input.Label htmlFor="twoNightsStopCheckboxSearchForm">
                  {t('Searchform.Stopover.TwoNightsStop')}
                </Input.Label>
              </Inline>
            </Inline>
          </Stack>
        </fieldset>
      )}
    </div>
  );
};

type ActionsType = {
  updateAllStopovers: (isStopoverEnabled: boolean) => void;
  updateStopover: (stopover: string, isSelected: boolean) => void;
};

type State = {
  form: FormStateMap;
};

const mapStateToProps: MapStateToProps<ConnectedStateProps, OwnProps, State> = (
  state,
  { form },
) => {
  const myFormValueSelector = formValueSelector(form);
  return {
    isTopStopoverControlSelected: myFormValueSelector(state, ENABLED),
    isShortStopoverSelected: myFormValueSelector(state, SHORT),
    isOneDayStopoverSelected: myFormValueSelector(state, ONE_DAY),
    isOneNightStopoverSelected: myFormValueSelector(state, ONE_NIGHT),
    isTwoNightsStopoverSelected: myFormValueSelector(state, TWO_NIGHTS),
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, { form: string }> = (
  dispatch,
  { form },
) => ({
  updateAllStopovers: (isStopoverEnabled: boolean) => {
    dispatch(changeFormValue(form, SHORT, isStopoverEnabled));
    dispatch(changeFormValue(form, ONE_DAY, isStopoverEnabled));
    dispatch(changeFormValue(form, ONE_NIGHT, isStopoverEnabled));
    dispatch(changeFormValue(form, TWO_NIGHTS, isStopoverEnabled));
  },
  updateStopover: (stopover: string, isSelected: boolean) => {
    dispatch(changeFormValue(form, stopover, isSelected));
  },
});

const mergeProps = (state: ConnectedStateProps, actions: ActionsType, props: OwnProps) => {
  return {
    ...state,
    ...actions,
    ...props,
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(StopoverSelect);
